/* eslint-disable camelcase */
import PluginContext from 'contexts/PluginContext';
import { useContext } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import Button from 'components/button/Button';
import { Generate } from '@squaredup/ids';
import { buttonVariants } from '@/components/Button';
import { getClientS3BucketUrl } from 'lib/getClientS3BucketUrl';
import Config from 'config';
import { ConfigEnvironment } from 'lib/configTypes';

interface AwsCreateRoleProps {
    name: string;
    validation?: RegisterOptions;
    disabled?: boolean;
}

const AWSAccountIds: Record<ConfigEnvironment, string> = {
    Local: '075277679322',
    Development: '075277679322',
    'Pre-Prod': '811305693429',
    Production: '755262121079',
    ProductionEU: '674331230786',
    ProductionUK: '841147116887'
};

/**
 * Displays a 'Create IAM Role' button for display in the AWS data source configuration.
 * 
 * The button launches the AWS console using a CloudFormation 'quick create' link that pre-populates a
 * CF template with the info necessary to create the role.  The role can then be 'assumed' by the SquaredUp
 * plugin to gain access to the customer AWS environment.
 * 
 * In other words the button helps to automate creation of the required role, to make it easier for the user
 * to get up and running with the AWS plugin.
 */
export default function AwsCreateRole(props: AwsCreateRoleProps) {
    const { register, setValue } = useFormContext();
    const isDisabled = props.disabled ?? false;
    register(props.name, props.validation);
    const pluginContext = useContext(PluginContext);

    const createRoleClicked = async () => {
        try {
            if (isDisabled) {
                return;
            }

            if (pluginContext.plugin === undefined) {
                throw new Error('pluginContext.plugin is undefined');
            }

            launchAwsConsoleWithCloudFormationTemplate();
        } catch (err: any) {
            setValue(
                props.name,
                {
                    userName: '',
                    errors: [err.response?.data?.error ?? err.message]
                },
                { shouldTouch: true, shouldDirty: true }
            );
        }
    };

    return (
        <div>
            <div className='flex items-center space-x-4 bg-componentBackgroundSecondary py-xxs px-md' data-testid='awsCreateRole'>
                {createRoleButton(createRoleClicked, isDisabled)}
            </div>
            <div className='mt-2 ml-2 text-sm leading-6'>
                Creates an IAM role with only required permissions, using CloudFormation.&nbsp;
                    <a
                        className={buttonVariants({ variant: 'link' })}
                        href='https://docs.squaredup.com/data-sources/aws-plugin#roles-users-with-programmatic-access'
                        target='_blank'
                        rel='noopener noreferrer'
                        {...props}
                    >
                    Learn more
                    </a>
                <br></br>
                Use the outputs from the CloudFormation deployment to fill in the details below.
            </div>
            
        </div>
    );
}

function createRoleButton(buttonClicked: () => Promise<void>, isDisabled?: boolean) {
    return (
        <div className='flex'>
            <svg 
                 xmlns='http://www.w3.org/2000/svg'
                 fill='none'
                 height='41'
                 width='41'
                 viewBox='0 0 30 30'
            >
                <rect width='30' height='30' fill='#FF9100'></rect>
                <path d='M10.6362 13.3582C10.6362 13.6052 10.6628 13.8054 10.7094 13.9522C10.7626 14.0991 10.8291 14.2592 10.9222 14.4328C10.9555 14.4862 10.9688 14.5396 10.9688 14.5863C10.9688 14.653 10.9289 14.7198 10.8424 14.7865L10.4234 15.0668C10.3635 15.1069 10.3036 15.1269 10.2504 15.1269C10.1839 15.1269 10.1174 15.0935 10.0509 15.0334C9.95773 14.9333 9.87791 14.8265 9.81139 14.7198C9.74487 14.6063 9.67836 14.4795 9.60519 14.326C9.08634 14.94 8.43446 15.247 7.64955 15.247C7.0908 15.247 6.64512 15.0868 6.31918 14.7665C5.99325 14.4461 5.82695 14.019 5.82695 13.485C5.82695 12.9177 6.0265 12.4572 6.43227 12.1102C6.83803 11.7631 7.37682 11.5896 8.06196 11.5896C8.28812 11.5896 8.52094 11.6096 8.76706 11.643C9.01317 11.6763 9.26594 11.7297 9.53202 11.7898V11.3026C9.53202 10.7954 9.42559 10.4416 9.21938 10.2347C9.00652 10.0278 8.64732 9.92771 8.13513 9.92771C7.90232 9.92771 7.66285 9.95441 7.41674 10.0145C7.17062 10.0745 6.93115 10.148 6.69834 10.2414C6.59191 10.2881 6.51209 10.3148 6.46553 10.3282C6.41896 10.3415 6.3857 10.3482 6.3591 10.3482C6.26597 10.3482 6.21941 10.2814 6.21941 10.1413V9.81425C6.21941 9.70746 6.23271 9.62737 6.26597 9.58065C6.29923 9.53393 6.3591 9.48721 6.45222 9.4405C6.68504 9.32036 6.96441 9.22025 7.29035 9.14016C7.61629 9.05339 7.96218 9.01335 8.32804 9.01335C9.1196 9.01335 9.69831 9.19355 10.0708 9.55396C10.4367 9.91436 10.6229 10.4616 10.6229 11.1958V13.3582H10.6362ZM7.93558 14.3727C8.15509 14.3727 8.38125 14.3327 8.62072 14.2526C8.86018 14.1725 9.07304 14.0256 9.25264 13.8254C9.35907 13.6986 9.43889 13.5585 9.4788 13.3983C9.51871 13.2381 9.54532 13.0445 9.54532 12.8176V12.5373C9.35242 12.4906 9.14621 12.4505 8.93335 12.4238C8.72049 12.3972 8.51429 12.3838 8.30808 12.3838C7.86241 12.3838 7.53647 12.4706 7.31696 12.6508C7.09745 12.831 6.99102 13.0846 6.99102 13.4183C6.99102 13.732 7.07084 13.9656 7.23714 14.1258C7.39678 14.2926 7.62959 14.3727 7.93558 14.3727ZM13.277 15.0935C13.1573 15.0935 13.0774 15.0735 13.0242 15.0268C12.971 14.9867 12.9244 14.8933 12.8845 14.7665L11.3214 9.60735C11.2814 9.47387 11.2615 9.3871 11.2615 9.34038C11.2615 9.2336 11.3147 9.17353 11.4211 9.17353H12.073C12.1994 9.17353 12.2859 9.19355 12.3324 9.24027C12.3856 9.28032 12.4256 9.37375 12.4655 9.50056L13.583 13.9189L14.6207 9.50056C14.6539 9.36708 14.6938 9.28032 14.747 9.24027C14.8003 9.20023 14.8934 9.17353 15.0131 9.17353H15.5453C15.6716 9.17353 15.7581 9.19355 15.8113 9.24027C15.8645 9.28032 15.9111 9.37375 15.9377 9.50056L16.9887 13.9723L18.1395 9.50056C18.1794 9.36708 18.2259 9.28032 18.2725 9.24027C18.3257 9.20023 18.4122 9.17353 18.5319 9.17353H19.1505C19.257 9.17353 19.3168 9.22692 19.3168 9.34038C19.3168 9.37375 19.3102 9.40712 19.3035 9.44717C19.2969 9.48721 19.2836 9.54061 19.257 9.61402L17.6539 14.7732C17.614 14.9066 17.5674 14.9934 17.5142 15.0334C17.461 15.0735 17.3745 15.1002 17.2614 15.1002H16.6894C16.563 15.1002 16.4765 15.0802 16.4233 15.0334C16.3701 14.9867 16.3235 14.9 16.2969 14.7665L15.2659 10.4616L14.2415 14.7598C14.2082 14.8933 14.1683 14.9801 14.1151 15.0268C14.0619 15.0735 13.9688 15.0935 13.849 15.0935H13.277ZM21.8246 15.2737C21.4787 15.2737 21.1328 15.2337 20.8002 15.1536C20.4676 15.0735 20.2082 14.9867 20.0352 14.8866C19.9288 14.8265 19.8556 14.7598 19.829 14.6997C19.8024 14.6397 19.7891 14.5729 19.7891 14.5129V14.1725C19.7891 14.0323 19.8423 13.9656 19.9421 13.9656C19.982 13.9656 20.0219 13.9723 20.0619 13.9856C20.1018 13.999 20.1616 14.0256 20.2281 14.0523C20.4543 14.1525 20.7004 14.2325 20.9598 14.2859C21.2259 14.3393 21.4853 14.366 21.7514 14.366C22.1705 14.366 22.4964 14.2926 22.7226 14.1458C22.9487 13.999 23.0685 13.7854 23.0685 13.5117C23.0685 13.3249 23.0086 13.1714 22.8889 13.0445C22.7691 12.9177 22.543 12.8043 22.217 12.6975L21.2525 12.3972C20.7669 12.2436 20.4077 12.0167 20.1882 11.7164C19.9687 11.4227 19.8556 11.0957 19.8556 10.7486C19.8556 10.4683 19.9155 10.2214 20.0352 10.0078C20.155 9.79423 20.3146 9.60735 20.5142 9.46052C20.7137 9.30701 20.9399 9.19355 21.206 9.11346C21.472 9.03337 21.7514 9 22.0441 9C22.1904 9 22.3434 9.00667 22.4898 9.0267C22.6428 9.04672 22.7824 9.07342 22.9221 9.10011C23.0552 9.13348 23.1816 9.16685 23.3013 9.2069C23.421 9.24694 23.5141 9.28699 23.5807 9.32703C23.6738 9.38043 23.7403 9.43382 23.7802 9.49389C23.8201 9.54728 23.8401 9.6207 23.8401 9.71414V10.0278C23.8401 10.168 23.7869 10.2414 23.6871 10.2414C23.6339 10.2414 23.5474 10.2147 23.4343 10.1613C23.0552 9.98778 22.6295 9.90101 22.1572 9.90101C21.778 9.90101 21.4787 9.96108 21.2725 10.0879C21.0663 10.2147 20.9598 10.4082 20.9598 10.6819C20.9598 10.8688 21.0264 11.0289 21.1594 11.1558C21.2924 11.2826 21.5386 11.4094 21.8911 11.5228L22.8357 11.8232C23.3146 11.9767 23.6605 12.1903 23.8667 12.4639C24.0729 12.7375 24.1727 13.0512 24.1727 13.3983C24.1727 13.6853 24.1128 13.9456 23.9997 14.1725C23.88 14.3994 23.7204 14.5996 23.5141 14.7598C23.3079 14.9267 23.0618 15.0468 22.7758 15.1336C22.4765 15.227 22.1638 15.2737 21.8246 15.2737Z' fill='white'></path><path fill-rule='evenodd' clip-rule='evenodd' d='M23.0818 18.5172C20.8933 20.1391 17.7138 21 14.9799 21C11.1484 21 7.69612 19.5784 5.0886 17.2158C4.8824 17.0289 5.06865 16.7753 5.31477 16.9221C8.13514 18.5639 11.614 19.5584 15.2127 19.5584C17.6406 19.5584 20.308 19.0512 22.7625 18.01C23.1283 17.8431 23.441 18.2503 23.0818 18.5172Z' fill='white'></path><path fill-rule='evenodd' clip-rule='evenodd' d='M23.993 17.4759C23.7136 17.1155 22.1438 17.3023 21.432 17.3891C21.2192 17.4158 21.1859 17.2289 21.3788 17.0888C22.6294 16.2078 24.6848 16.4614 24.9242 16.7551C25.1637 17.0554 24.8577 19.1177 23.687 20.1055C23.5074 20.259 23.3345 20.1789 23.4143 19.9787C23.6803 19.3179 24.2724 17.8296 23.993 17.4759Z' fill='white'></path>
            </svg>
            <Button 
                variant='secondary' 
                onClick={buttonClicked}
                disabled={isDisabled}
            >
                Create IAM Role
            </Button>
        </div>
    );
}

function launchAwsConsoleWithCloudFormationTemplate() {
    const uniqSuffix = createDateBasedUniquifier();
    const stackName = `SquaredUpAccessRoleStack-${uniqSuffix}`;

    // The template yaml we pass to CloudFormation is stored in S3 in the client bucket. 
    // Normally we'd access this via the app URL, e.g. https://app.squaredup.com/aws-iam-role-cf-template.yaml,
    // but it seems CloudFormation requires the underlying S3 URL.
    const templateURL = `${getClientS3BucketUrl()}/aws-iam-role-cf-template.yaml`;
    const roleName = `SquaredUpAccessRole-${uniqSuffix}`;
    const externalId = Generate();
    const squpAwsAccountId = AWSAccountIds[Config.Environment as ConfigEnvironment] ?? AWSAccountIds['Production'];

    // Create a CloudFormation 'quick-create' link.
    // See https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/cfn-console-create-stacks-quick-create-links.html
    const searchParams = new URLSearchParams({
        stackName,
        templateURL,
        param_RoleName: roleName,
        param_ExternalId: externalId,
        param_SquaredUpAWSAccountId: squpAwsAccountId
    });

    const awsConsoleUrl = new URL(`https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?${searchParams}`);

    window.open(awsConsoleUrl.toString(), '_blank', 'noopener,noreferrer');
}

/**
 * Create a suffix to append to a string to make it unique (as long as suffixes are generated at least one millisecond 
 * apart). The suffix gives an indication of when the suffix was created by using today's date as the start of the 
 * suffix. The rest of the suffix is the number of milliseconds since the start of today, which has zero padding 
 * so can be used for sorting by time generated.
 * 
 * For example 2024-10-02-00123456
 */
function createDateBasedUniquifier() {
    const now = new Date();    
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed, so add 1
    const day = String(now.getDate()).padStart(2, '0');
    
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const millisecondsSinceStartOfDay = now.valueOf() - startOfDay.valueOf();
    
    return `${year}-${month}-${day}-${millisecondsSinceStartOfDay.toString().padStart(8, '0')}`;    
}